import { createTheme, alpha } from '@mui/material/styles';

// ============================================================================
// Tournament Engine — "Crimson Violet" theme
// Dark base · frosted glass · red→purple gradient signature.
// ============================================================================

// Custom MUI variants used by this theme need to be declared so TypeScript
// accepts `<Paper variant="glass">` and `<Chip variant="soft">`.
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    glass: true;
  }
}
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}

// --- signature tokens (reuse these in sx props anywhere) --------------------
export const GRADIENT = 'linear-gradient(135deg, #f43f5e 0%, #a855f7 100%)';
export const GRADIENT_GLOW = '0 8px 24px rgba(244,63,94,0.32)';
export const GLASS_BG =
  'linear-gradient(165deg, rgba(50,28,42,0.55), rgba(22,14,22,0.50))';
export const GLASS_BORDER = 'rgba(244,80,140,0.14)';

// Drop-in helper for any frosted panel: <Box sx={glassSx}>. borderRadius: 2 is
// an sx multiplier (→ ~22px via shape.borderRadius), not a raw px value.
export const glassSx = {
  background: GLASS_BG,
  border: `1px solid ${GLASS_BORDER}`,
  backdropFilter: 'blur(22px)',
  WebkitBackdropFilter: 'blur(22px)',
  borderRadius: 2,
  boxShadow: '0 24px 64px rgba(0,0,0,0.50)',
} as const;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#f43f5e', light: '#fb7185', dark: '#e11d48', contrastText: '#ffffff' },
    secondary: { main: '#a855f7', light: '#c084fc', dark: '#9333ea', contrastText: '#ffffff' },
    success: { main: '#4ade80', light: '#86efac', dark: '#22c55e', contrastText: '#06210f' }, // win / qualified / finished
    error: { main: '#fb7185', light: '#fda4af', dark: '#f43f5e', contrastText: '#ffffff' }, // loss
    warning: { main: '#f0b429', contrastText: '#3a2a00' }, // rank-2 / podium gold
    background: { default: '#0e0a0f', paper: '#161016' },
    divider: GLASS_BORDER,
    text: {
      primary: '#f6f0f3',
      secondary: '#c8b8c2',
      disabled: '#8a7681',
    },
  },

  shape: { borderRadius: 11 },

  typography: {
    // The Noto fallbacks carry the glyphs the localised UI needs and Plus
    // Jakarta Sans lacks — Khmer, Cyrillic (Noto Sans) and Simplified Chinese
    // (Noto Sans SC); the browser uses them only for codepoints Plus Jakarta
    // Sans cannot render.
    fontFamily:
      '"Plus Jakarta Sans", "Noto Sans Khmer", "Noto Sans", "Noto Sans SC", system-ui, -apple-system, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 800, letterSpacing: '-0.01em' },
    h6: { fontWeight: 800, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 700 },
    subtitle2: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: 0 },
    overline: { fontWeight: 700, letterSpacing: '0.07em' },
  },

  components: {
    // --- page background: dark base + two red/purple glow blobs --------------
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0e0a0f',
          backgroundImage:
            'radial-gradient(1100px 500px at 85% -12%, rgba(244,63,94,0.20), transparent 60%),' +
            'radial-gradient(900px 520px at -8% 114%, rgba(168,85,247,0.15), transparent 60%)',
          backgroundAttachment: 'fixed',
        },
        // lets you write <span className="msym">account_tree</span>
        '.msym': {
          fontFamily: "'Material Symbols Outlined'",
          fontWeight: 'normal',
          lineHeight: 1,
          verticalAlign: 'middle',
        },
      },
    },

    // --- frosted glass surfaces --------------------------------------------
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'transparent',
        },
      },
      variants: [
        {
          props: { variant: 'glass' },
          style: {
            background: GLASS_BG,
            border: `1px solid ${GLASS_BORDER}`,
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.50)',
          },
        },
      ],
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          background: GLASS_BG,
          border: `1px solid ${GLASS_BORDER}`,
          backdropFilter: 'blur(22px)',
          WebkitBackdropFilter: 'blur(22px)',
          borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,0.50)',
        },
      },
    },

    // --- dialogs / popups: solid surface so they don't read as transparent --
    // The MuiPaper root override above forces a transparent background, which
    // leaves a bare Dialog (CellEditor, Share, Bracket, Delete) showing the page
    // behind it. Give the dialog surface a solid black fill + visible border.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#000',
          backgroundImage: 'none',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,0.65)',
        },
      },
    },

    // --- menus / Select dropdowns: solid surface ---------------------------
    // Same problem as dialogs above — the transparent MuiPaper root leaves
    // menu popups (the language picker, best-of Selects) showing the page
    // behind them, so the options are unreadable. Give the menu surface a
    // solid fill, border and shadow.
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#171019',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0))',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 20px 56px rgba(0,0,0,0.6)',
        },
      },
    },

    // --- Autocomplete popups: solid surface --------------------------------
    // Same problem again, third surface: the Autocomplete listbox rides its
    // own Popper + Paper (neither a MuiDialog nor a MuiMenu), so the
    // transparent MuiPaper root left its options floating over whatever sat
    // behind the popup (see the sport-profile Formats picker). Same solid
    // fill as menus.
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: '#171019',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0))',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 20px 56px rgba(0,0,0,0.6)',
        },
      },
    },

    // --- top app bar: glass ------------------------------------------------
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgba(42,20,32,0.72), rgba(22,12,20,0.55))',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          borderBottom: `1px solid ${GLASS_BORDER}`,
        },
      },
    },

    // --- buttons -----------------------------------------------------------
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 11, paddingInline: 16, paddingBlock: 9 },
        // primary CTA → gradient + glow
        containedPrimary: {
          background: GRADIENT,
          boxShadow: GRADIENT_GLOW,
          '&:hover': {
            background: GRADIENT,
            filter: 'brightness(1.08)',
            boxShadow: '0 10px 30px rgba(244,63,94,0.42)',
          },
        },
        // secondary action (e.g. "Print score sheets")
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
          backgroundColor: 'rgba(255,255,255,0.04)',
          color: '#fda4af',
          '&:hover': {
            borderColor: alpha('#fb7185', 0.5),
            backgroundColor: 'rgba(255,255,255,0.06)',
          },
        },
        text: { color: '#fda4af' },
      },
    },

    // --- chips (pill metadata + status) ------------------------------------
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 999 },
        outlined: {
          borderColor: 'rgba(255,255,255,0.10)',
          backgroundColor: 'rgba(255,255,255,0.04)',
          color: '#e6d6dd',
        },
      },
      variants: [
        {
          props: { variant: 'soft', color: 'success' },
          style: {
            backgroundColor: alpha('#22c55e', 0.16),
            color: '#4ade80',
            fontWeight: 700,
            border: 'none',
          },
        },
        {
          props: { variant: 'soft', color: 'error' },
          style: {
            backgroundColor: alpha('#f43f5e', 0.16),
            color: '#fb7185',
            fontWeight: 700,
            border: 'none',
          },
        },
      ],
    },

    // --- tabs: gradient indicator + active text ----------------------------
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 0 },
        indicator: {
          height: 2,
          borderRadius: 2,
          background: GRADIENT,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          letterSpacing: '0.07em',
          fontSize: 12,
          minHeight: 0,
          padding: '8px 4px',
          color: '#8a7681',
          '&.Mui-selected': { color: '#fb7185' },
        },
      },
    },

    // --- misc tidy-ups -----------------------------------------------------
    MuiDivider: { styleOverrides: { root: { borderColor: GLASS_BORDER } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: 'rgba(22,12,20,0.95)',
          border: `1px solid ${GLASS_BORDER}`,
          fontSize: 12,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        bar: { background: GRADIENT },
        root: { backgroundColor: 'rgba(255,255,255,0.06)' },
      },
    },
  },
});

export default theme;
